import { useCallback, useState } from 'react'

export const LOCATION_ERROR_CODES = {
  UNSUPPORTED: 'UNSUPPORTED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  POSITION_UNAVAILABLE: 'POSITION_UNAVAILABLE',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN'
}

const readSessionCoordsFromStorage = () => {
  try {
    const raw = localStorage.getItem('location')
    if (!raw) return null

    const parsed = JSON.parse(raw)
    const lat = Number(parsed?.lat)
    const lng = Number(parsed?.lng)

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null
    }

    return { lat, lng }
  } catch {
    return null
  }
}

const mapGeoErrorCode = (geoError) => {
  if (!geoError || typeof geoError.code !== 'number') {
    return LOCATION_ERROR_CODES.UNKNOWN
  }

  if (geoError.code === 1) return LOCATION_ERROR_CODES.PERMISSION_DENIED
  if (geoError.code === 2) return LOCATION_ERROR_CODES.POSITION_UNAVAILABLE
  if (geoError.code === 3) return LOCATION_ERROR_CODES.TIMEOUT
  return LOCATION_ERROR_CODES.UNKNOWN
}

const useSessionLocation = () => {
  const [sessionCoords, setSessionCoords] = useState(() => readSessionCoordsFromStorage())
  const [locationErrorCode, setLocationErrorCode] = useState(null)

  const clearLocationError = useCallback(() => {
    setLocationErrorCode(null)
  }, [])

  const getPosition = (options) =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options)
    })

  const requestLocation = useCallback(async () => {
    if (!navigator?.geolocation) {
      const errorCode = LOCATION_ERROR_CODES.UNSUPPORTED
      setLocationErrorCode(errorCode)
      return { ok: false, errorCode, coords: null }
    }

    const toCoords = (position) => ({
      lat: Number(position.coords.latitude),
      lng: Number(position.coords.longitude)
    })

    let position
    try {
      position = await getPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 })
    } catch (firstError) {
      if (firstError?.code !== 2) {
        const errorCode = mapGeoErrorCode(firstError)
        setLocationErrorCode(errorCode)
        return { ok: false, errorCode, coords: null }
      }
      try {
        position = await getPosition({ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 })
      } catch (retryError) {
        const errorCode = mapGeoErrorCode(retryError)
        setLocationErrorCode(errorCode)
        return { ok: false, errorCode, coords: null }
      }
    }

    const coords = toCoords(position)

    if (!Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) {
      const errorCode = LOCATION_ERROR_CODES.UNKNOWN
      setLocationErrorCode(errorCode)
      return { ok: false, errorCode, coords: null }
    }

    setSessionCoords(coords)
    setLocationErrorCode(null)
    return { ok: true, coords, errorCode: null }
  }, [])

  return {
    sessionCoords,
    locationErrorCode,
    requestLocation,
    clearLocationError
  }
}

export default useSessionLocation
